import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ShoppingBag, Tag, Truck, X, MapPin } from "lucide-react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { useConfig } from "../context/ConfigContext";
import { createOrder, validateDiscount, API } from "../lib/api";
import { toast } from "sonner";

const Checkout = () => {
  const { items, totals, clear } = useCart();
  const navigate = useNavigate();
  const { ecommerceEnabled, loading: configLoading } = useConfig();
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shippingInfo, setShippingInfo] = useState({ flat_cost: 5.95, free_threshold: 60 });
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    street: "",
    house_number: "",
    postal_code: "",
    city: "",
    notes: "",
    age_confirmed: false,
    terms_accepted: false,
    newsletter_signup: true, // default-aan zoals gevraagd
    pickup: false,
  });
  const [discountInput, setDiscountInput] = useState("");
  const [discount, setDiscount] = useState(null);
  const [applyingCode, setApplyingCode] = useState(false);

  useEffect(() => {
    axios.get(`${API}/shipping-info`).then((r) => setShippingInfo(r.data)).catch(() => {});
  }, []);

  const subtotal = totals.subtotal;
  const discountAmount = discount ? discount.discount_amount : 0;
  const afterDiscount = Math.max(0, subtotal - discountAmount);
  let shipping = 0;
  if (!form.pickup && items.length > 0 && afterDiscount < shippingInfo.free_threshold) {
    shipping = shippingInfo.flat_cost;
  }
  const grandTotal = Math.round((afterDiscount + shipping) * 100) / 100;

  const update = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const applyCode = async () => {
    if (!discountInput.trim()) return;
    setApplyingCode(true);
    try {
      const res = await validateDiscount(discountInput.trim(), subtotal);
      setDiscount(res);
      toast.success(res.message);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Code ongeldig";
      toast.error(typeof msg === "string" ? msg : "Code ongeldig");
      setDiscount(null);
    } finally {
      setApplyingCode(false);
    }
  };

  const removeCode = () => {
    setDiscount(null);
    setDiscountInput("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.age_confirmed) {
      toast.error("Bevestig dat je 18+ bent.");
      return;
    }
    if (!form.terms_accepted) {
      toast.error("Je moet akkoord gaan met de algemene voorwaarden.");
      return;
    }
    if (items.length === 0) {
      toast.error("Je winkelmandje is leeg.");
      return;
    }
    if (form.pickup && !form.phone.trim()) {
      toast.error("Voer een telefoonnummer in zodat we contact kunnen opnemen voor de afhaalafspraak.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          units: i.units || 1,
        })),
        discount_code: discount?.code || null,
      };
      const res = await createOrder(payload);
      if (res?.checkout_url) {
        clear();
        // Redirect to Mollie hosted checkout (GET navigation)
        window.location.href = res.checkout_url;
        return;
      }
      toast.error("Betaalverwijzing niet ontvangen — probeer opnieuw.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Bestelling mislukt.";
      toast.error(typeof msg === "string" ? msg : "Bestelling mislukt.", { duration: 10000 });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="checkout-success" className="py-24">
        <div className="mx-auto max-w-2xl px-5 sm:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 border-2 border-[var(--tha-red)] rounded-full mb-8 pulse-red">
            <Check size={28} className="text-[var(--tha-red)]" />
          </div>
          <h1 className="font-display text-5xl sm:text-7xl leading-none">
            BEDANKT! <span className="neon-red-text">PROOST.</span>
          </h1>
          <p className="mt-6 text-[var(--tha-muted)]">
            Bestelling <span className="font-mono-x text-white">#{submitted.id.slice(0, 8).toUpperCase()}</span>{" "}
            {submitted.pickup
              ? "ontvangen. We nemen binnen 24 uur contact met je op om een afhaaltijd af te spreken in Assen."
              : "ontvangen. We sturen \u2019m zo snel mogelijk jouw kant op."}
          </p>
          <div className="mt-8 border border-[var(--tha-border)] p-6 text-left">
            <div className="text-xs font-mono-x tracking-widest text-[var(--tha-muted)]">SAMENVATTING</div>
            <div className="mt-4 space-y-2 text-sm">
              {submitted.items.map((i) => (
                <div key={i.product_id} className="flex justify-between">
                  <span>{i.quantity}× {i.name}</span>
                  <span>€ {(i.quantity * i.price).toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t border-[var(--tha-border)] pt-3 mt-3 space-y-1">
                <Line label="Subtotaal" value={`€ ${submitted.subtotal.toFixed(2)}`} />
                {submitted.discount_amount > 0 && (
                  <Line
                    label={`Korting (${submitted.discount_code})`}
                    value={`- € ${submitted.discount_amount.toFixed(2)}`}
                    accent
                  />
                )}
                <Line
                  label={submitted.shipping_cost > 0 ? "Verzending" : "Verzending (gratis)"}
                  value={`€ ${submitted.shipping_cost.toFixed(2)}`}
                />
              </div>
              <div className="border-t border-[var(--tha-border)] pt-3 mt-3 flex justify-between font-display text-xl">
                <span>TOTAAL</span>
                <span>€ {submitted.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate("/")}
            className="mt-10 btn-ghost-red px-7 py-3 font-display tracking-wider"
            data-testid="back-home-button"
          >
            TERUG NAAR HOME
          </button>
        </div>
      </div>
    );
  }

  if (!configLoading && !ecommerceEnabled) {
    return (
      <div data-testid="checkout-disabled" className="py-28">
        <div className="mx-auto max-w-2xl px-5 sm:px-8 text-center">
          <h1 className="font-display text-5xl sm:text-6xl leading-none">
            ONLINE BESTELLEN IS <span className="neon-red-text">EVEN DICHT.</span>
          </h1>
          <p className="mt-6 text-[var(--tha-muted)]">
            Je kunt je bestelling wel aanvragen via de shop — we nemen snel contact met je op.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/shop" data-testid="checkout-disabled-shop-link" className="btn-red px-7 py-3 font-display tracking-wider">
              NAAR DE SHOP
            </Link>
            <Link to="/zakelijk" data-testid="checkout-disabled-b2b-link" className="btn-ghost-red px-7 py-3 font-display tracking-wider">
              ZAKELIJK
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="relative">
      <section className="py-20 border-b border-[var(--tha-border)]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-[var(--tha-red)]" />
            <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">AFREKENEN</span>
          </div>
          <h1 className="font-display text-5xl sm:text-7xl mt-5 leading-none">
            BIJNA <span className="neon-red-text">VAN JOU.</span>
          </h1>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <form
            data-testid="checkout-form"
            onSubmit={submit}
            className="lg:col-span-7 border border-[var(--tha-border)] bg-[#0a0a0a] p-6 sm:p-10 space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="VOLLEDIGE NAAM" required>
                <input data-testid="checkout-name" required value={form.full_name} onChange={update("full_name")} className="w-full px-4 py-3 text-sm" />
              </Field>
              <Field label="E-MAIL" required>
                <input data-testid="checkout-email" required type="email" value={form.email} onChange={update("email")} className="w-full px-4 py-3 text-sm" />
              </Field>
            </div>
            <Field label={form.pickup ? "TELEFOON *" : "TELEFOON (OPTIONEEL)"}>
              <input data-testid="checkout-phone" required={form.pickup} value={form.phone} onChange={update("phone")} className="w-full px-4 py-3 text-sm" />
            </Field>

            {/* Delivery method picker */}
            <div className="border-t border-[var(--tha-border)] pt-5">
              <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-3">
                BEZORGMETHODE
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label
                  data-testid="checkout-method-ship"
                  className={`flex items-start gap-3 p-4 cursor-pointer border transition-colors ${
                    !form.pickup
                      ? "border-[var(--tha-red)] bg-[#1a0000]"
                      : "border-[var(--tha-border)] hover:border-[var(--tha-muted)]"
                  }`}
                >
                  <input
                    type="radio"
                    name="delivery"
                    checked={!form.pickup}
                    onChange={() => setForm((f) => ({ ...f, pickup: false }))}
                    className="mt-1 accent-[var(--tha-red)] w-4 h-4"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 font-display text-lg">
                      <Truck size={16} /> VERZENDEN
                    </div>
                    <div className="text-xs text-[var(--tha-muted)] mt-1">
                      DHL · € {shippingInfo.flat_cost.toFixed(2)} · gratis vanaf € {Math.round(shippingInfo.free_threshold)}
                    </div>
                  </div>
                </label>
                <label
                  data-testid="checkout-method-pickup"
                  className={`flex items-start gap-3 p-4 cursor-pointer border transition-colors ${
                    form.pickup
                      ? "border-[var(--tha-red)] bg-[#1a0000]"
                      : "border-[var(--tha-border)] hover:border-[var(--tha-muted)]"
                  }`}
                >
                  <input
                    type="radio"
                    name="delivery"
                    checked={form.pickup}
                    onChange={() => setForm((f) => ({ ...f, pickup: true }))}
                    className="mt-1 accent-[var(--tha-red)] w-4 h-4"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 font-display text-lg">
                      <MapPin size={16} /> OPHALEN IN ASSEN
                    </div>
                    <div className="text-xs text-[var(--tha-muted)] mt-1">
                      Gratis · wij nemen contact op voor afhaaltijd
                    </div>
                  </div>
                </label>
              </div>
              {form.pickup && (
                <div
                  data-testid="checkout-pickup-notice"
                  className="mt-4 border border-[var(--tha-red)] bg-[#1a0000] p-4 text-sm text-[var(--tha-muted)]"
                >
                  <span className="text-[var(--tha-red)] font-mono-x text-[10px] tracking-widest">
                    INFO ·
                  </span>{" "}
                  We nemen binnen 24 uur contact met je op via{" "}
                  <span className="text-white">{form.phone || "je telefoonnummer"}</span> om een
                  afhaaltijd af te spreken in Assen. Geen verzendkosten.
                </div>
              )}
            </div>

            {!form.pickup && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div className="sm:col-span-2">
                    <Field label="STRAAT" required>
                      <input data-testid="checkout-street" required={!form.pickup} value={form.street} onChange={update("street")} className="w-full px-4 py-3 text-sm" />
                    </Field>
                  </div>
                  <Field label="NR." required>
                    <input data-testid="checkout-house" required={!form.pickup} value={form.house_number} onChange={update("house_number")} className="w-full px-4 py-3 text-sm" />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <Field label="POSTCODE" required>
                    <input data-testid="checkout-postal" required={!form.pickup} value={form.postal_code} onChange={update("postal_code")} className="w-full px-4 py-3 text-sm" />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="STAD" required>
                      <input data-testid="checkout-city" required={!form.pickup} value={form.city} onChange={update("city")} className="w-full px-4 py-3 text-sm" />
                    </Field>
                  </div>
                </div>
              </>
            )}
            <Field label="OPMERKINGEN (OPTIONEEL)">
              <textarea data-testid="checkout-notes" value={form.notes} onChange={update("notes")} rows={3} className="w-full px-4 py-3 text-sm resize-none" />
            </Field>

            <label className="flex items-start gap-3 cursor-pointer pt-3">
              <input
                data-testid="checkout-age-confirm"
                type="checkbox"
                checked={form.age_confirmed}
                onChange={update("age_confirmed")}
                className="mt-1 accent-[var(--tha-red)] w-4 h-4"
              />
              <span className="text-sm text-[var(--tha-muted)]">
                Ik bevestig dat ik <span className="text-white">18 jaar of ouder</span> ben en ik ga akkoord met de verkoop van alcoholische producten.
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                data-testid="checkout-terms"
                type="checkbox"
                checked={form.terms_accepted}
                onChange={update("terms_accepted")}
                className="mt-1 accent-[var(--tha-red)] w-4 h-4"
              />
              <span className="text-sm text-[var(--tha-muted)]">
                Ik ga akkoord met de{" "}
                <Link to="/voorwaarden" target="_blank" className="text-[var(--tha-red)] underline">
                  algemene voorwaarden
                </Link>{" "}
                en het{" "}
                <Link to="/privacy" target="_blank" className="text-[var(--tha-red)] underline">
                  privacybeleid
                </Link>
                .
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                data-testid="checkout-newsletter"
                type="checkbox"
                checked={form.newsletter_signup}
                onChange={update("newsletter_signup")}
                className="mt-1 accent-[var(--tha-red)] w-4 h-4"
              />
              <span className="text-sm text-[var(--tha-muted)]">
                Houd me op de hoogte via de nieuwsbrief — drops, events en deals (kan altijd worden afgemeld).
              </span>
            </label>

            <button
              data-testid="checkout-submit"
              disabled={loading}
              className="btn-red w-full py-4 font-display tracking-wider text-base disabled:opacity-60"
            >
              {loading ? "DOORSTUREN NAAR BETALING..." : `BETAAL MET IDEAL — € ${grandTotal.toFixed(2)}`}
            </button>

            <p className="text-[10px] font-mono-x tracking-widest text-[var(--tha-dim)] text-center">
              VEILIG BETALEN VIA MOLLIE — iDEAL, BANCONTACT, CREDITCARD
            </p>
          </form>

          <aside className="lg:col-span-5">
            <div className="border border-[var(--tha-border)] bg-[#0a0a0a] p-6 sm:p-8 sticky top-28">
              <div className="flex items-center gap-3">
                <ShoppingBag size={16} className="text-[var(--tha-red)]" />
                <div className="font-display text-2xl">JOUW BESTELLING</div>
              </div>

              {items.length === 0 ? (
                <p data-testid="checkout-empty" className="mt-8 text-sm text-[var(--tha-muted)]">
                  Je mandje is leeg. Voeg een product toe via de{" "}
                  <span onClick={() => navigate("/shop")} className="text-[var(--tha-red)] cursor-pointer">shop</span>.
                </p>
              ) : (
                <>
                  <div className="mt-6 space-y-3">
                    {items.map((i) => (
                      <div key={i.product_id} className="flex justify-between text-sm border-b border-[var(--tha-border)] pb-3">
                        <div className="min-w-0 pr-3">
                          <div className="text-white truncate">{i.name}</div>
                          <div className="text-xs text-[var(--tha-muted)]">
                            {i.quantity} × € {i.price.toFixed(2)}
                          </div>
                        </div>
                        <div className="text-white shrink-0">€ {(i.quantity * i.price).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>

                  {/* Discount input */}
                  <div className="mt-6">
                    <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] flex items-center gap-2">
                      <Tag size={12} /> KORTINGSCODE
                    </div>
                    {discount ? (
                      <div
                        data-testid="discount-applied"
                        className="mt-2 flex items-center justify-between border border-[var(--tha-red)] bg-[#1a0303] px-3 py-2 text-sm"
                      >
                        <span className="text-white">
                          <span className="text-[var(--tha-red)] font-mono-x">{discount.code}</span>{" "}
                          — {discount.percent}% korting
                        </span>
                        <button
                          onClick={removeCode}
                          data-testid="remove-discount"
                          className="text-[var(--tha-muted)] hover:text-white"
                          aria-label="Verwijder code"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="mt-2 flex">
                        <input
                          data-testid="discount-input"
                          value={discountInput}
                          onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                          placeholder="BV. FAMILIE10"
                          className="flex-1 px-3 py-2 text-sm font-mono-x"
                        />
                        <button
                          data-testid="apply-discount-button"
                          type="button"
                          onClick={applyCode}
                          disabled={applyingCode || !discountInput}
                          className="btn-ghost-red px-4 py-2 text-xs font-mono-x tracking-widest disabled:opacity-50"
                        >
                          {applyingCode ? "..." : "TOEPASSEN"}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 space-y-1.5 text-sm">
                    <Line label="Subtotaal" value={`€ ${subtotal.toFixed(2)}`} />
                    {discountAmount > 0 && (
                      <Line label={`Korting (${discount.code})`} value={`- € ${discountAmount.toFixed(2)}`} accent />
                    )}
                    <Line
                      label={
                        <span className="inline-flex items-center gap-1">
                          {form.pickup ? <MapPin size={12} /> : <Truck size={12} />}
                          {form.pickup ? "Ophalen in Assen" : shipping === 0 ? "Verzending (gratis)" : "Verzending"}
                        </span>
                      }
                      value={form.pickup ? "GRATIS" : `€ ${shipping.toFixed(2)}`}
                    />
                  </div>

                  <div className="mt-4 flex justify-between items-baseline border-t border-[var(--tha-border)] pt-4">
                    <span className="text-xs font-mono-x tracking-widest text-[var(--tha-muted)]">TOTAAL</span>
                    <span data-testid="checkout-total" className="font-display text-3xl text-white">
                      € {grandTotal.toFixed(2)}
                    </span>
                  </div>
                  {!form.pickup && afterDiscount < shippingInfo.free_threshold && (
                    <div className="mt-3 text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                      NOG € {(shippingInfo.free_threshold - afterDiscount).toFixed(2)} TOT GRATIS VERZENDING
                    </div>
                  )}
                </>
              )}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
      {label}{required && " *"}
    </label>
    <div className="mt-2">{children}</div>
  </div>
);

const Line = ({ label, value, accent }) => (
  <div className="flex justify-between text-sm">
    <span className="text-[var(--tha-muted)]">{label}</span>
    <span className={accent ? "text-[#22c55e]" : "text-white"}>{value}</span>
  </div>
);

export default Checkout;
